import React, { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { useIntl, getLocale, useSelector } from 'umi';
import Filter from './components/Filter';
import StoreRankDetailsCore from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import useStoreRankFilterExtend from '@/pages/intelligence/hooks/useStoreRankFilterExtend';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useStoreRankDetailsCoreUI from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/hooks/useStoreRankDetailsCoreUI';
import { getStoreRankDetailsCoreUIData } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/utils/dataTransfer';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import useInitialTopChartsName from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useInitialTopChartsName';
import { fetchStoreRanksDetailsData } from '@/pages/intelligence/services/storeRank';
import { fetchPCStoreRankDetailsCountriesRanksPlus } from './services';
import { getPCDataSourceInfo } from '@/pages/intelligence/utils/getDataSourceInfo';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import {
  analysisPlatformTopInfo,
  getTopSource,
} from '@/pages/intelligence/_config/storeRank/utils';
import { getIsSectionalRank, getParams, handleResData, getInitialFetchParams } from './utils';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import {
  PC_SOURCES,
  PC_STORE_RANK_TOPS_NAMES_MAPPING,
} from '@/pages/intelligence/_config/storeRank/config';
import '@/pages/intelligence/assets/scss/StoreRanks/details.scss';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import type { IStoreRankDetailsProps } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetails';
import type {
  IStoreRankDetailsResData,
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountriesRanks,
} from '@/pages/intelligence/types/storeRank';
import type { IStoreRankDetailsFilter } from './types';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import TipContext from '@home/components/TipContextComponents/TipContext';
import { PC_ENTITY_TYPE, STEAM_PLATFORM, PC_PLATFORMS } from '@intelligence/v3/constants/topcharts';
import './index.scss';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';
import { formatDateStr } from '@intelligence/v3/utils/date';
interface IPCStoreRankDetailsProps extends IStoreRankDetailsProps {
  getRadioOptionsTips?: Function;
}

const DEFAULT_FILTER = {
  granularity: 'daily',
  start_date: '',
  end_date: '',

  platform: STEAM_PLATFORM,
  top: 'top sellers',

  region_type: 'region',
  regions: ['global'],
};

const StoreRank: React.FC<IPCStoreRankDetailsProps> = (props) => {
  const { entityType = PC_ENTITY_TYPE, gameId, basicInfo, getRadioOptionsTips } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isLoading, setIsLoading] = useState(true);
  const [storeRankDetailsCoreResData, setStoreRankDetailsCoreResData] = useState<
    IStoreRankDetailsResData[]
  >([]);

  const [currentFetchSource, setCurrentFetchSource] = useState<string[]>([]);

  const { regionsOptions: regionOptions } = useStoreTopRegionOptionsMap(STEAM_PLATFORM, {
    entityType,
    platforms: PC_PLATFORMS,
  });

  // const filterState = useSelector((state: any) => state?.[FILTER_EXTEND_NAMESPACE]);
  const {
    isOperateDetail,
    detailFilterExtend: filterExtend,
    setDetailFilterExtend: setFilterExtend,
  } = useStoreRankFilterExtend({});
  // const { isOperateDetail, detailFilterExtend,setFilterExtend } = useFilterExtend({});
  const { detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});

  const initialSource = useInitialTopChartsName(PC_SOURCES);
  const { platform: initialPlatform, top: initialTop } = analysisPlatformTopInfo(initialSource);
  const defaultFilter = {
    ...DEFAULT_FILTER,
    platform: initialPlatform || STEAM_PLATFORM,
    top: initialTop || 'top sellers',
  };
  const [formRankInit, setFormRankInit] = useState(true);
  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const isFromRank = sessionStorage.getItem('isFromRank');
  const weekNotOperate = isFromRank === 'true';

  const extendDateType = filterExtend?.rangeTime?.granularity;
  const extendDateNum = extendDateType === 'weekly' ? 27 : 30;
  if (formRankInit && isFromRank && filterExtend) {
    if (moment(filterExtend?.rangeTime?.end_date).isValid()) {
      filterExtend.rangeTime.start_date = moment(filterExtend?.rangeTime?.end_date)
        .subtract(extendDateNum, 'd')
        .format('YYYY-MM-DD');
    }
    setFormRankInit(false);
  }
  if (detailFilterExtend?.rangeTime?.granularity === 'weekly') {
    // 获取当前日期
    const currentDate = moment();

    // 获取当前周的周一日期（如果当前日期不是周一）
    const currentWeekMonday = currentDate.clone().day(1); // 1代表周一
    // 获取四周前的周二日期
    // 首先，找到当前日期所在周的周二
    const thisWeekTuesday = currentDate.clone().day(2); // 2代表周二
    // 然后，回溯到四周前
    const fourWeeksAgoTuesday = thisWeekTuesday.subtract(4, 'weeks');
    detailFilterExtend.rangeTime.end_date = currentWeekMonday.format('YYYY-MM-DD');
    detailFilterExtend.rangeTime.start_date = fourWeeksAgoTuesday.format('YYYY-MM-DD');
  }
  const initialFilter = getInitialFetchParams(
    isOperateDetail,
    weekNotOperate ? filterExtend : detailFilterExtend,
    defaultFilter,
  );
  // const initialFilter = getInitialFetchParams(isOperateDetail, detailFilterExtend, defaultFilter);
  const [filter, setFilter] = useState<IStoreRankDetailsFilter>(initialFilter);

  const { earliestPeriod, updateEarliestDateData, baseReleaseTimeValue } = useEarliestTime({
    entityType,
    basicInfo,
    topchartsState,
  });

  const downloadParams = useMemo(() => {
    const params = getParams(filter);
    return {
      entity_type: entityType,
      app_id: gameId ?? '',
      app_ids: gameId ? [gameId] : [],
      date_type: params?.granularity,
      format: params?.granularity,
      start_date: params?.startDate,
      end_date: params?.endDate,
      region_type: params?.regionType,
      regions: params?.regions,
      platform: filter?.platform || '',
    };
  }, [entityType, gameId, filter]);

  // 是否为分段排名模式(steam top sellers 并且 勾选国家非global（国家数量大于1个时）按排名分段展示)
  const isSectionalRank = getIsSectionalRank({
    platform: filter?.platform,
    top: filter?.top,
    regions: filter?.regions,
  });

  const platformInfo = getPCDataSourceInfo(filter?.platform);
  // const topNameLocaleKey = PC_STORE_RANK_TOPS_NAMES_MAPPING?.[filter?.top];
  // const topNameLocale = formatMessage({ id: topNameLocaleKey });
  const { visualisOption, tablesOption } = useStoreRankDetailsCoreUI({
    isLoading,
    isSectionalRank,
    visualisHelpperOptionConfig: {
      isShowYAxisName: true,
      yAxisName: isSectionalRank
        ? formatMessage({ id: 'dbi_count' })
        : formatMessage({ id: 'dbi_rank' }),
      // tooltipHeaderLeftTitle: toFirstUpperCase(filter?.top),
      // PC 游戏平台折线图不展示顶部指标名称
      // tooltipHeaderLeftTitle: topNameLocale,
      tooltipHeaderLeftTitle: ' ',
      tooltipHeaderLeftIcon: platformInfo?.icon,
      rankDimensionsNameFormatter: (dimension: string) => {
        // const { platform, top } = analysisPlatformTopInfo(dimension);
        const { top } = analysisPlatformTopInfo(dimension);
        // const platformInfo = getPCDataSourceInfo(platform);
        const topNameLocaleKey = PC_STORE_RANK_TOPS_NAMES_MAPPING?.[top];
        let topNameLocale;
        try {
          topNameLocale = formatMessage({ id: topNameLocaleKey });
        } catch (exception) {
          topNameLocale = dimension;
        }
        // return [platformInfo?.name, topNameLocale].join('_');
        return topNameLocale;
      },
    },
    tablesHelpperOptionConfig: {
      rankDimensionsNameFormatter: (dimension: string) => {
        const { top } = analysisPlatformTopInfo(dimension);
        const topNameLocaleKey = PC_STORE_RANK_TOPS_NAMES_MAPPING?.[top];
        let topNameLocale;
        try {
          topNameLocale = formatMessage({ id: topNameLocaleKey });
        } catch (exception) {
          topNameLocale = dimension;
        }
        // return toFirstUpperCase(top);
        return topNameLocale;
      },
    },
    data: storeRankDetailsCoreResData,
  });
  const firstWeekVisualisOption = useMemo(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return visualisOption;
    }
    const firstWeekSinceReleaseDates = getFirstWeekSinceReleaseDates({
      dateType: filter?.granularity,
      startDate: filter?.start_date,
      endDate: filter?.end_date,
      releaseDate: baseReleaseTimeValue,
    });
    if (!firstWeekSinceReleaseDates.length) {
      return visualisOption;
    }
    const responseDataMap = new Map<string, IStoreRankDetailsResData>(
      storeRankDetailsCoreResData.map((item): [string, IStoreRankDetailsResData] => {
        return [formatDateStr(item?.date), item];
      }),
    );
    const displayData = firstWeekSinceReleaseDates.map((date) => {
      const dateItem = responseDataMap.get(date);
      if (dateItem) {
        return { ...dateItem, date };
      }
      return { date, metric: [] };
    });
    const { sourceDataDimensions, visualisSourceData } = getStoreRankDetailsCoreUIData(displayData);
    return {
      ...visualisOption,
      sourceDataDimensions,
      sourceData: visualisSourceData,
    };
  }, [
    topchartsState?.selectedTimeShortcutType,
    visualisOption,
    storeRankDetailsCoreResData,
    filter?.granularity,
    filter?.start_date,
    filter?.end_date,
    baseReleaseTimeValue,
  ]);

  const updateStoreRankDetailsCoreData = async (filterPayload?: IStoreRankDetailsFilter) => {
    setIsLoading(true);
    const { granularity, startDate, endDate, sources, regions } = getParams(filter, filterPayload);

    setCurrentFetchSource(sources);

    const paramValue = {
      entity_type: entityType,
      source: sources,
      // 这个字段仅在mobile有效，pc、console传空，防止接口有必传校验导致接口报错（三端同一个接口）
      unified_id: '',
      app_ids: [gameId],
      // region_type: regionType,
      region_type: 'market',
      regions,
      granularity,
      start_date: startDate,
      end_date: endDate,
    };
    try {
      const res = await fetchStoreRanksDetailsData(paramValue);
      const dateTypeReq = res?.req?.granularity ?? 'daily';
      const startDateReq = res?.req?.start_date ?? '';
      const endDateReq = res?.req?.end_date ?? '';
      const filterTmp = {
        ...filter,
        ...filterPayload,
        granularity: dateTypeReq,
        start_date: startDateReq,
        end_date: endDateReq,
      };
      // 是否为分段排名模式(steam top sellers 并且 勾选国家非global（国家数量大于1个时）按排名分段展示)
      const isSectionalRank = getIsSectionalRank({
        platform: filterTmp?.platform,
        top: filterTmp?.top,
        regions: filterTmp?.regions,
      });
      const storeRankDetailsCoreResDataTmp = handleResData(res?.data, { isSectionalRank });
      setFilter(filterTmp);
      setStoreRankDetailsCoreResData(storeRankDetailsCoreResDataTmp);

      // 更新最早有数据时间
      updateEarliestDateData({
        entity_type: paramValue.entity_type,
        data_sources: paramValue.source,
        app_ids: paramValue.app_ids,
        metric: ['rank'],
        unified_id: paramValue?.unified_id,
        region_type: paramValue.region_type,
        regions: paramValue.regions,
        dateType: paramValue.granularity,
      });
    } catch (exception) {
      setStoreRankDetailsCoreResData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 分段排名时，国家排名子表格 获取数据的方法
  const fetchCountriesRankData = async (
    params: IStoreRankDetailsTablesSourceData,
  ): Promise<IStoreRankDetailsCountriesRanks[]> => {
    const { granularity, sources, regions } = getParams(filter);
    const res = await fetchPCStoreRankDetailsCountriesRanksPlus({
      edition_id: gameId,
      source: sources,
      regions,
      granularity,
      end_date: params?.date ?? '',
    });
    return res;
  };

  useEffect(() => {
    if (!gameId) return;
    updateStoreRankDetailsCoreData();
  }, [gameId]);

  useEffect(
    () => () => {
      sessionStorage.removeItem('isFromRank');
    },
    [],
  );

  return (
    <div className="dbi-store-rank-details-page">
      <Filter
        className="dbi-store-rank-details-page_filter"
        regionOptions={regionOptions}
        getRadioOptionsTips={getRadioOptionsTips}
        filter={filter}
        onConfirm={(payload: any) => {
          const filterTmp = {
            ...filter,
            ...payload,
          };

          updateStoreRankDetailsCoreData(filterTmp);
          setFilter(filterTmp);
          setDetailFilterExtend(filterTmp);
          setFilterExtend(filterTmp);
          const source = getTopSource(filterTmp?.platform, filterTmp?.top);
          updateURLSearchParams({ source });
        }}
        onReset={() => {}}
        sinceReleaseTime={baseReleaseTimeValue}
        earliestDataTime={earliestPeriod}
      />
      <StoreRankDetailsCore
        isLoading={isLoading}
        gameId={gameId}
        // 这个title 看pc交互 是固定写死的，但是mobile又是动态的，看后面需不需要改为跟mobile一致
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatMessage({ id: 'dbi_store_rank_visualis_card_title_single' })}
            <TipContext
              className="tip-text-hd dbi_pc_store_rank_tips"
              tipText={formatMessage({ id: 'dbi_info_rank_trend_tip_desc' })}
            />
          </div>
        }
        visualisOption={firstWeekVisualisOption}
        tablesOption={tablesOption}
        pageLogConfig={dbiPageLogMes.pcGameDetailsStoreRank}
        fetchCountriesRankData={fetchCountriesRankData}
        currentFetchSource={currentFetchSource}
        downloadBtn={
          <ExportTable
            classModalName=""
            entityType="pc"
            // downloadModalSize={820}
            downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
            isShowMaxDownloadRowsTips={false}
            // @ts-ignore
            businessType="pc-details-store-rank"
            isDisableDownload={isLoading}
            downloadLogConfigKey="pcGameDetailsStoreRankDownloadExcel"
            total={tablesOption?.sourceData?.length ?? 0}
            tableParams={downloadParams}
            newStyleFileName={`${basicInfo?.name}_Store Rank`}
          />
        }
      />
    </div>
  );
};

export default StoreRank;
